<template>
  <div id="player-details-view">
    <v-carousel-transition>
      <v-container
        v-if="player.loading"
        class="d-flex justify-center align-center my-16"
      >
        <v-progress-circular
          indeterminate
          size="75"
        />
      </v-container>
      <v-row
        v-else-if="player.data"
        no-gutters
        justify="center"
      >
        <v-col
          xl="11"
          md="12"
        >
          <player-details-card
            :player="player.data"
            :stats="player.stats"
            @edit:hr-profile="hrProfile.dialog = true"
          />

          <v-row
            class="mt-4 ml-2"
            align="center"
          >
            <v-col class="d-flex align-center">
              <span class="title">
                {{ $t("player_profile.analyze.label") }}
              </span>
              <v-btn
                class="ml-2 text-none"
                text
                color="primary"
                @click="navigateTo(RouteNames.PLAYER_SMART_ASSISTANT)"
              >
                {{ player.data.smart_assistant.length ? $tc('button.active_alerts', player.data.smart_assistant.length, { count: player.data.smart_assistant.length }) : $t('button.no_alerts_today') }}
              </v-btn>
            </v-col>
            <v-col
              v-if="hasPlayerTimezones && player.data && player.data.settings && player.data.settings.timezone"
              cols="auto"
            >
              <div class="caption grey--text text--lighten-1 mt-4 text-right">
                {{ $t('player_profile.timezone_label') }}: <strong>{{ timezones.find(tz => tz.tzCode === player.data.settings.timezone).name }}</strong>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col
              v-for="route in routes"
              :key="route.name"
              cols="3"
            >
              <v-card
                height="160"
                rounded="lg"
                @click="navigateTo(route.name)"
              >
                <v-card-text class="d-flex flex-column justify-center align-center fill-height">
                  <img
                    :src="route.icon"
                    style="width: 60px; height: 60px;"
                  >
                  <div class="title grey--text text--darken-4 mt-4">
                    {{ route.label }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <div v-else>
        {{ $t('alert.something_went_wrong') }}
      </div>
    </v-carousel-transition>

    <performance-profile-dialog
      v-if="player.data"
      :visible.sync="hrProfile.dialog"
      :data="player.data"
      type="player"
      @save="updatePlayerPerformanceProfile"
    />
  </div>
</template>

<script>
import { PlayerApiService } from '@/data/services'
import PlayerDetailsCard from '@/components/players/PlayerDetailsCard'
import LineChart from '@/components/charts/LineChart'
import PlayerTrainingLoadTable from '@/components/players/PlayerTrainingLoadTable'
import {
  convertAccelerationToImperial,
  convertSpeed,
  getUnitSuffixForUser
} from '@/utils/helpers'
import RadarChart from '@/components/charts/RadarChart'
import PlayerEventAttendanceView from '@/views/player/PlayerEventAttendanceView.vue'
import PainAvatarGroup from '@/components/helpers/PainAvatarGroup.vue'
import PlayerMediaView from '@/views/player/PlayerMediaView.vue'
import PlayerNotesView from '@/views/player/PlayerNotesView.vue'
import PlayerDiagnosticView from '@/views/player/PlayerDiagnosticView.vue'
import PlayerTrainingsView from '@/views/player/PlayerTrainingsView.vue'
import PlayerMedicalView from '@/views/player/PlayerMedicalView.vue'
import PlayerSmartAssistantAlertsView from '@/views/player/PlayerSmartAssistantAlertsView.vue'
import PerformanceProfileDialog from '@/components/players/PerformanceProfileDialog.vue'
import timezones from 'timezones-list'
import { RouteNames } from '@/router/routes'
import PlayerLoadView from '@/components/players/PlayerLoadView.vue'
import attendance from '@/assets/img/player_info/Attendance.svg'
import diagnostics from '@/assets/img/player_info/Diagnostics.svg'
import events from '@/assets/img/player_info/Events.svg'
import exercises from '@/assets/img/player_info/Exercises.svg'
import load from '@/assets/img/player_info/Load.svg'
import medical from '@/assets/img/player_info/Medical.svg'
import overview from '@/assets/img/player_info/Overview.svg'
import wellness from '@/assets/img/player_info/Wellness.svg'
import Subtitle from '@/components/helpers/Subtitle.vue'

export default {
  name: 'PlayerDetailsView',
  // eslint-disable-next-line vue/no-unused-components
  components: { Subtitle, PlayerTrainingLoadView: PlayerLoadView, PlayerSmartAssistantAlertsView, PerformanceProfileDialog, PlayerMedicalView, PlayerTrainingsView, PlayerDiagnosticView, PlayerNotesView, PlayerMediaView, PainAvatarGroup, PlayerEventAttendanceView, RadarChart, PlayerTrainingLoadTable, LineChart, PlayerDetailsCard },
  data: () => ({
    view: 'load',
    player: {
      data: null,
      loading: false
    },
    hrProfile: {
      dialog: false
    },
    customizeModal: false,
    dateRange: null,
    attendance,
    diagnostics,
    events,
    exercises,
    load,
    medical,
    overview,
    wellness
  }),
  computed: {
    RouteNames() {
      return RouteNames
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasMedicalModule() {
      return this.user && this.user.organisation_settings && this.user.organisation_settings.has_medical_module
    },
    hasDiagnosticsModule() {
      return this.user && this.user.organisation_settings && this.user.organisation_settings.has_diagnostics_module
    },
    hasItlModule() {
      return this.user.organisation_settings.has_itl_module || false
    },
    hasTrainingModule() {
      return this.user.organisation_settings.has_training_builder_module || false
    },
    hasPlayerTimezones() {
      return this.user.organisation_settings.has_player_timezones || false
    },
    hasGpsModule() {
      return this.user.organisation_settings.has_gps_module || false
    },
    hasHeartRateModule() {
      return this.user.organisation_settings.has_heart_rate_module || false
    },
    routes() {
      return [
        { name: RouteNames.PLAYER_OVERVIEW, active: this.hasItlModule || this.hasDiagnosticsModule, icon: this.overview, label: this.$t('player_profile.overview') },
        { name: RouteNames.PLAYER_LOAD, active: this.hasItlModule || this.hasGpsModule, icon: this.load, label: this.$t('player_profile.load') },
        { name: RouteNames.PLAYER_WELLNESS, active: this.hasItlModule, icon: this.wellness, label: this.$t('player_profile.wellness') },
        { name: RouteNames.PLAYER_TRAINING_EXERCISES, active: this.hasTrainingModule, icon: this.exercises, label: this.$t('player_profile.exercises') },
        { name: RouteNames.PLAYER_DIAGNOSTICS, active: this.hasDiagnosticsModule, icon: this.diagnostics, label: this.$t('player_profile.diagnostics') },
        { name: RouteNames.PLAYER_MEDICAL, active: this.hasMedicalModule, icon: this.medical, label: this.$t('player_profile.medical') },
        { name: RouteNames.PLAYER_EVENTS, active: this.hasGpsModule || this.hasHeartRateModule, icon: this.events, label: this.$t('player_profile.events') },
        { name: RouteNames.PLAYER_ATTENDANCE, active: true, icon: this.attendance, label: this.$t('player_profile.attendance') }
      ].filter(route => route.active)
    }
  },
  watch: {
    '$route.query.view': function (newVal, oldVal) {
      if (newVal === 'trainings') {
        this.view = 'trainings'
      } else if (newVal === 'trainingLoad') {
        this.view = 'load'
      }

      if (this.$route.query.from) {
        this.dateRange = {
          from: this.$route.query.from,
          to: this.$route.query.to
        }

        this.$router.replace({ query: {} })
      }

      this.fetchPlayerDetails()
    }
  },
  created() {
    this.timezones = timezones

    this.fetchPlayerDetails()
  },
  methods: {
    async fetchPlayerDetails() {
      try {
        this.player.loading = true

        const resp = await PlayerApiService.getPlayerDetails(this.$route.params.id)
        this.player.data = resp.data

        for (const zone of Object.keys(this.player.data.speed_profile)) {
          this.player.data.speed_profile[zone].lower = convertSpeed(this.player.data.speed_profile[zone].lower, getUnitSuffixForUser('m/s'))
          this.player.data.speed_profile[zone].upper = convertSpeed(this.player.data.speed_profile[zone].upper, getUnitSuffixForUser('m/s'))
        }

        for (const zone of Object.keys(this.player.data.acceleration_profile)) {
          this.player.data.acceleration_profile[zone].lower = convertAccelerationToImperial(this.player.data.acceleration_profile[zone].lower)
          this.player.data.acceleration_profile[zone].upper = convertAccelerationToImperial(this.player.data.acceleration_profile[zone].upper)
        }
      } catch (e) {
        // todo
      } finally {
        this.player.loading = false
      }
    },
    async updatePlayerPerformanceProfile(data) {
      try {
        this.saving = true

        await PlayerApiService.updatePerformanceProfile(this.player.data.id, {
          hr_profile: data.hr_profile,
          speed_profile: data.speed_profile,
          acceleration_profile: data.acceleration_profile
        })

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.performance_profile_update.success'),
          color: 'success'
        })

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.performance_profile_update.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.saving = false
      }
    },
    navigateTo(route) {
      this.$router.push({ name: route, params: { id: this.$route.params.id } })
    }

  }
}
</script>
